// Template picker with search. Used by the SMS composer (fetches SMS templates
// itself) and by the email composer, which passes its own templates in via the
// `templates` prop — see the props block on the component below.
import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaFileAlt, FaTimes, FaSearch } from 'react-icons/fa';
import { getTemplates } from '../services/followUpService';

const Wrapper = styled.div`
  position: relative;
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: ${p => p.theme.colors.surface};
  border: 1.5px solid ${p => p.selected ? p.theme.colors.primary : p.theme.colors.inputGray}44;
  border-radius: ${p => p.theme.borderRadius};
  color: ${p => p.selected ? p.theme.colors.textPrimary : p.theme.colors.textSecondary};
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${p => p.theme.colors.primary}88;
  }

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
  }

  .name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
`;

const ClearBtn = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${p => p.theme.colors.inputGray}44;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 10px;
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.error}33;
    color: ${p => p.theme.colors.error};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  background: ${p => p.theme.colors.surface};
  border: 1.5px solid ${p => p.theme.colors.primary}44;
  border-radius: ${p => p.theme.borderRadius};
  z-index: 50;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
`;

const Option = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 10px 14px;
  background: ${p => p.active ? p.theme.colors.primary + '18' : 'transparent'};
  border: none;
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}22;
  color: ${p => p.theme.colors.textPrimary};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${p => p.theme.colors.primary}18;
  }

  &:last-child {
    border-bottom: none;
  }

  .opt-name {
    font-size: 0.88rem;
    font-weight: 500;
    margin-bottom: 2px;
  }

  .opt-preview {
    font-size: 0.78rem;
    color: ${p => p.theme.colors.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .opt-category {
    display: inline-block;
    font-size: 0.68rem;
    padding: 1px 6px;
    border-radius: 4px;
    background: ${p => p.theme.colors.primary}22;
    color: ${p => p.theme.colors.primary};
    margin-left: 8px;
    font-weight: 500;
  }
`;

const EmptyMsg = styled.div`
  padding: 20px;
  text-align: center;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.85rem;
`;

// Sticky so the search box stays reachable while scrolling a long list.
const SearchRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: ${p => p.theme.colors.surface};
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}33;

  svg {
    color: ${p => p.theme.colors.textSecondary};
    flex-shrink: 0;
  }

  input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.86rem;

    &::placeholder {
      color: ${p => p.theme.colors.textSecondary};
    }
  }
`;

const ClearSearch = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${p => p.theme.colors.inputGray}44;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 9px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${p => p.theme.colors.error}33;
    color: ${p => p.theme.colors.error};
  }
`;

/**
 * @param {function} onSelect      called with the template, or null when cleared
 * @param {number}   selectedId    currently selected template id
 * @param {array}    [templates]   supply templates instead of fetching SMS ones
 *                                 (the email composer does this)
 * @param {string}   [label]       field label
 * @param {string}   [emptyMessage] shown when there are no templates at all
 * @param {function} [getPreview]  derives the grey preview line from a template
 */
const TemplateSelector = ({
  onSelect,
  selectedId = null,
  templates: externalTemplates = null,
  label = 'Load Template',
  emptyMessage = 'No templates yet. Create one in Follow-Ups → SMS Templates.',
  getPreview = (t) => t.body,
}) => {
  const [fetchedTemplates, setFetchedTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  const isExternal = Array.isArray(externalTemplates);
  const templates = isExternal ? externalTemplates : fetchedTemplates;

  useEffect(() => {
    // Templates supplied by the parent — nothing to fetch.
    if (isExternal) return;

    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const data = await getTemplates();
        setFetchedTemplates(data);
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [isExternal]);

  // Search across everything visible in a row, so typing "quote" finds a match
  // whether it's in the name, the category or the body text.
  const visibleTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => {
      const preview = getPreview(t) || '';
      return `${t.name || ''} ${t.category || ''} ${preview}`.toLowerCase().includes(q);
    });
  }, [templates, search, getPreview]);

  // Focus the search box when the dropdown opens so you can just start typing.
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = templates.find(t => t.id === selectedId);

  const handleSelect = (template) => {
    onSelect(template);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null);
    setOpen(false);
  };

  return (
    <Wrapper ref={ref}>
      <Label>{label}</Label>
      <SelectButton
        type="button"
        onClick={() => setOpen(!open)}
        selected={!!selected}
      >
        <span className="left">
          <FaFileAlt size={13} />
          <span className="name">
            {selected ? selected.name : 'Choose a template...'}
          </span>
        </span>
        <span className="right">
          {selected && <ClearBtn onClick={handleClear}><FaTimes /></ClearBtn>}
          <FaChevronDown size={12} />
        </span>
      </SelectButton>

      {open && (
        <Dropdown>
          {templates.length > 0 && (
            <SearchRow>
              <FaSearch size={11} />
              <input
                ref={searchRef}
                type="text"
                value={search}
                placeholder="Search templates…"
                onChange={(e) => setSearch(e.target.value)}
                // Enter picks the only remaining match — quick keyboard path.
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setOpen(false); }
                  if (e.key === 'Enter' && visibleTemplates.length === 1) {
                    e.preventDefault();
                    handleSelect(visibleTemplates[0]);
                  }
                }}
              />
              {search && (
                <ClearSearch type="button" onClick={() => { setSearch(''); searchRef.current?.focus(); }}>
                  <FaTimes />
                </ClearSearch>
              )}
            </SearchRow>
          )}

          {loading ? (
            <EmptyMsg>Loading templates...</EmptyMsg>
          ) : templates.length === 0 ? (
            <EmptyMsg>{emptyMessage}</EmptyMsg>
          ) : visibleTemplates.length === 0 ? (
            <EmptyMsg>No templates match “{search}”.</EmptyMsg>
          ) : (
            visibleTemplates.map(t => (
              <Option
                key={t.id}
                active={t.id === selectedId}
                onClick={() => handleSelect(t)}
              >
                <div>
                  <span className="opt-name">{t.name}</span>
                  {t.category && <span className="opt-category">{t.category}</span>}
                </div>
                <span className="opt-preview">{getPreview(t)}</span>
              </Option>
            ))
          )}
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default TemplateSelector;
